import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { BookingDateCalendar } from "@/components/BookingDateCalendar";
import { BookingDateInput } from "@/components/BookingDateInput";
import { clampDateToMin, formatLocaleDate } from "@/lib/dates";
import { BOOKING_DATE_MAX } from "@/lib/dateInput";
import { useLocale, type BookingLocale } from "@/i18n";
import { isIosTouchDevice, isMobileViewport } from "@/lib/platform";
import {
  CALENDAR_POPOVER_ENTER,
  CALENDAR_POPOVER_SPRING,
  DURATION,
  EASE_OUT_EXPO,
  usePrefersReducedMotion,
} from "@/lib/motion";

type ActiveField = "arrival" | "departure";

const DESKTOP_PANEL_WIDTH = 280;

export interface BookingPeriodDateFieldsProps {
  arrivalId: string;
  departureId: string;
  arrivalLabel?: string;
  departureLabel?: string;
  arrival: string;
  departure: string;
  arrivalMin: string;
  departureMin: string;
  onArrivalChange: (value: string) => void;
  onDepartureChange: (value: string) => void;
  onArrivalSelected?: (value: string) => void;
  /** Deaktiviert Kalendertage je nach Feld (z. B. außerhalb der Buchungssaison). */
  isDateDisabled?: (iso: string, field: ActiveField) => boolean;
}

function openNativeDatePicker(input: HTMLInputElement | null) {
  if (!input || input.disabled) return;
  if (isMobileViewport()) {
    input.closest("label")?.scrollIntoView({ block: "center", behavior: "smooth" });
  }
  try {
    input.showPicker();
  } catch {
    input.focus();
    input.click();
  }
}

function scrollDateFieldIntoView(element: HTMLElement | null) {
  if (!isMobileViewport() || !element) return;
  element.closest("label")?.scrollIntoView({ block: "center", behavior: "smooth" });
}

function DateDisplay({
  value,
  locale,
  placeholder,
}: {
  value: string;
  locale: BookingLocale;
  placeholder: string;
}) {
  return (
    <span className={clsx("bk-date-field__display bk-input", !value && "text-slate-400")}>
      <span className="block font-medium text-slate-900">
        {value ? formatLocaleDate(value, locale) : placeholder}
      </span>
    </span>
  );
}

export function BookingPeriodDateFields({
  arrivalId,
  departureId,
  arrivalLabel,
  departureLabel,
  arrival,
  departure,
  arrivalMin,
  departureMin,
  onArrivalChange,
  onDepartureChange,
  onArrivalSelected,
  isDateDisabled,
}: BookingPeriodDateFieldsProps) {
  const { messages: m, locale } = useLocale();
  const resolvedArrivalLabel = arrivalLabel ?? m.common.arrival;
  const resolvedDepartureLabel = departureLabel ?? m.common.departure;
  const reducedMotion = usePrefersReducedMotion();

  const useIosCalendar = isIosTouchDevice();
  const useCustomCalendar = useIosCalendar || isDateDisabled != null;
  const arrivalLabelId = useId();
  const departureLabelId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const calendarPanelRef = useRef<HTMLDivElement>(null);
  const arrivalAnchorRef = useRef<HTMLDivElement>(null);
  const departureAnchorRef = useRef<HTMLDivElement>(null);
  const departureInputRef = useRef<HTMLInputElement>(null);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [activeField, setActiveField] = useState<ActiveField>("arrival");
  const [popoverPos, setPopoverPos] = useState<{ left: number; top: number } | null>(null);
  const [fieldSlidePulse, setFieldSlidePulse] = useState(false);
  const prevActiveFieldRef = useRef<ActiveField>("arrival");

  const departureDisabled = !arrival;
  const mobileCalendar = isMobileViewport();

  function measurePopoverPosition(field: ActiveField) {
    const wrapper = wrapperRef.current;
    const anchor =
      field === "arrival" ? arrivalAnchorRef.current : departureAnchorRef.current;
    if (!wrapper || !anchor) return null;

    const wrapperRect = wrapper.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const left =
      field === "arrival"
        ? anchorRect.left - wrapperRect.left
        : anchorRect.right - wrapperRect.left - DESKTOP_PANEL_WIDTH;

    return {
      left: Math.max(0, Math.min(left, wrapperRect.width - DESKTOP_PANEL_WIDTH)),
      top: anchorRect.bottom - wrapperRect.top + 4,
    };
  }

  useLayoutEffect(() => {
    if (!calendarOpen || mobileCalendar) return;
    const pos = measurePopoverPosition(activeField);
    if (pos) setPopoverPos(pos);
  }, [calendarOpen, activeField, mobileCalendar, arrival, departure]);

  useEffect(() => {
    if (!calendarOpen || mobileCalendar || prevActiveFieldRef.current === activeField) {
      prevActiveFieldRef.current = activeField;
      return;
    }
    prevActiveFieldRef.current = activeField;
    setFieldSlidePulse(true);
    const timer = window.setTimeout(() => setFieldSlidePulse(false), 520);
    return () => window.clearTimeout(timer);
  }, [activeField, calendarOpen, mobileCalendar]);

  useEffect(() => {
    if (!calendarOpen) return;
    function onPointerDown(event: PointerEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setCalendarOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [calendarOpen]);

  useEffect(() => {
    if (!calendarOpen || !useCustomCalendar || !mobileCalendar) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [calendarOpen, useCustomCalendar, mobileCalendar]);

  useEffect(() => {
    if (!calendarOpen || mobileCalendar) return;
    function onReposition() {
      const pos = measurePopoverPosition(activeField);
      if (pos) setPopoverPos(pos);
    }
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [calendarOpen, activeField, mobileCalendar]);

  useEffect(() => {
    if (!useCustomCalendar && arrival && !departure) {
      const id = requestAnimationFrame(() => {
        openNativeDatePicker(departureInputRef.current);
      });
      return () => cancelAnimationFrame(id);
    }
  }, [arrival, departure, useCustomCalendar]);

  function applyArrival(next: string) {
    if (!next) {
      onArrivalChange("");
      return;
    }
    const iso = clampDateToMin(next, arrivalMin);
    if (isDateDisabled?.(iso, "arrival")) return;
    onArrivalChange(iso);
    onArrivalSelected?.(iso);
  }

  function applyDeparture(next: string) {
    if (!next) {
      onDepartureChange("");
      return;
    }
    const iso = clampDateToMin(next, departureMin);
    if (isDateDisabled?.(iso, "departure")) return;
    onDepartureChange(iso);
  }

  function openCalendar(field: ActiveField) {
    if (field === "departure" && departureDisabled) return;
    setActiveField(field);
    if (!mobileCalendar) {
      const pos = measurePopoverPosition(field);
      if (pos) setPopoverPos(pos);
    }
    setCalendarOpen(true);
  }

  function handleIosSelect(next: string) {
    if (activeField === "arrival") {
      applyArrival(next);
      setActiveField("departure");
      return;
    }
    applyDeparture(next);
    setCalendarOpen(false);
  }

  const gridClass = "grid min-w-0 gap-3 sm:grid-cols-2";

  if (useCustomCalendar) {
    const calendarValue = activeField === "arrival" ? arrival : departure;
    const calendarMin = activeField === "arrival" ? arrivalMin : departureMin;
    const calendarLabelId = activeField === "arrival" ? arrivalLabelId : departureLabelId;
    const calendarDateDisabled = isDateDisabled
      ? (iso: string) => isDateDisabled(iso, activeField)
      : undefined;

    const calendarProps = {
      value: calendarValue,
      min: calendarMin,
      isDateDisabled: calendarDateDisabled,
      rangeStart: arrival || undefined,
      rangeEnd:
        arrival && (departure || (activeField === "departure" ? calendarValue : ""))
          ? departure || calendarValue
          : undefined,
      labelledBy: calendarLabelId,
      onChange: handleIosSelect,
    };

    function renderMobileCalendarPanel() {
      if (!calendarOpen || !mobileCalendar) return null;
      return (
        <div ref={calendarPanelRef} className="bk-date-calendar-panel">
          <BookingDateCalendar {...calendarProps} />
        </div>
      );
    }

    return (
      <div ref={wrapperRef} className={clsx(gridClass, "relative")}>
        <label htmlFor={arrivalId} id={arrivalLabelId} className="block min-w-0 text-sm">
          <span className="mb-1 block font-medium text-slate-700">{resolvedArrivalLabel}</span>
          <div
            ref={arrivalAnchorRef}
            className={clsx(
              "bk-date-field-anchor",
              calendarOpen && activeField === "arrival" && "bk-date-field-anchor--open",
            )}
          >
            <div
              className={clsx(
                "bk-date-field",
                calendarOpen && activeField === "arrival" && "bk-date-field--open",
              )}
            >
              <button
                id={arrivalId}
                type="button"
                className="bk-date-field__trigger"
                aria-haspopup="dialog"
                aria-expanded={calendarOpen && activeField === "arrival"}
                aria-labelledby={arrivalLabelId}
                onClick={() => openCalendar("arrival")}
              >
                <DateDisplay
                  value={arrival}
                  locale={locale}
                  placeholder={m.common.selectDate}
                />
              </button>
            </div>
          </div>
        </label>

        <label htmlFor={departureId} id={departureLabelId} className="block min-w-0 text-sm">
          <span className="mb-1 block font-medium text-slate-700">{resolvedDepartureLabel}</span>
          <div
            ref={departureAnchorRef}
            className={clsx(
              "bk-date-field-anchor",
              departureDisabled && "opacity-60",
              calendarOpen && activeField === "departure" && "bk-date-field-anchor--open",
            )}
          >
            <div
              className={clsx(
                "bk-date-field",
                departureDisabled && "bk-date-field--disabled",
                calendarOpen && activeField === "departure" && "bk-date-field--open",
              )}
            >
              <button
                id={departureId}
                type="button"
                className="bk-date-field__trigger"
                disabled={departureDisabled}
                aria-haspopup="dialog"
                aria-expanded={calendarOpen && activeField === "departure"}
                aria-labelledby={departureLabelId}
                onClick={() => openCalendar("departure")}
              >
                <DateDisplay
                  value={departure}
                  locale={locale}
                  placeholder={m.common.selectDate}
                />
              </button>
            </div>
          </div>
        </label>

        <AnimatePresence>
          {!mobileCalendar && calendarOpen && popoverPos ? (
            <motion.div
              key="desktop-calendar"
              ref={calendarPanelRef}
              data-anchor={activeField}
              className="bk-date-calendar-panel bk-date-calendar-panel--popover bk-date-calendar-panel--floating"
              style={{ width: DESKTOP_PANEL_WIDTH }}
              initial={
                reducedMotion
                  ? false
                  : { opacity: 0, y: -12, scale: 0.92, left: popoverPos.left, top: popoverPos.top }
              }
              animate={{
                opacity: 1,
                y: 0,
                scale: fieldSlidePulse && !reducedMotion ? 1.02 : 1,
                left: popoverPos.left,
                top: popoverPos.top,
              }}
              exit={
                reducedMotion
                  ? { opacity: 0 }
                  : { opacity: 0, y: -10, scale: 0.94, transition: { duration: DURATION.fast } }
              }
              transition={{
                left: CALENDAR_POPOVER_SPRING,
                top: CALENDAR_POPOVER_SPRING,
                opacity: { duration: DURATION.normal, ease: EASE_OUT_EXPO },
                scale: reducedMotion ? { duration: 0 } : CALENDAR_POPOVER_ENTER,
                y: reducedMotion ? { duration: 0 } : CALENDAR_POPOVER_ENTER,
              }}
            >
              <BookingDateCalendar {...calendarProps} />
            </motion.div>
          ) : null}
        </AnimatePresence>

        {mobileCalendar && calendarOpen ? (
          <AnimatePresence>
            <button
              key="backdrop"
              type="button"
              className="bk-date-calendar-backdrop"
              aria-label="Kalender schließen"
              onClick={() => setCalendarOpen(false)}
            />
            {renderMobileCalendarPanel()}
          </AnimatePresence>
        ) : null}
      </div>
    );
  }

  return (
    <div className={gridClass}>
      <label htmlFor={arrivalId} className="block min-w-0 text-sm">
        <span className="mb-1 block font-medium text-slate-700">{resolvedArrivalLabel}</span>
        <div className="bk-date-field">
          <DateDisplay value={arrival} locale={locale} placeholder={m.common.selectDate} />
          <BookingDateInput
            id={arrivalId}
            className="bk-date-field__input"
            value={arrival}
            min={arrivalMin}
            max={BOOKING_DATE_MAX}
            onFocus={(e) => scrollDateFieldIntoView(e.currentTarget)}
            onChange={applyArrival}
          />
        </div>
      </label>

      <label htmlFor={departureId} className="block min-w-0 text-sm">
        <span className="mb-1 block font-medium text-slate-700">{resolvedDepartureLabel}</span>
        <div className={clsx("bk-date-field", departureDisabled && "bk-date-field--disabled")}>
          <DateDisplay value={departure} locale={locale} placeholder={m.common.selectDate} />
          <BookingDateInput
            ref={departureInputRef}
            id={departureId}
            className="bk-date-field__input"
            value={departure}
            min={departureMin}
            max={BOOKING_DATE_MAX}
            onFocus={(e) => scrollDateFieldIntoView(e.currentTarget)}
            onChange={applyDeparture}
            disabled={departureDisabled}
          />
        </div>
      </label>
    </div>
  );
}
